<script setup>
import {ref, computed, onMounted} from 'vue'
import axios from 'axios'
import OnboardingSettingsForm from './OnboardingSettingsForm.vue'
import FormTemplateService from "@/services/FormTemplateService";
import ContractTypeService from "@/services/ContractTypeService";
import SettingsService from "@/services/SettingsService";
import {showSuccessMessage} from "@/helper";
import DocumentTemplateService from "@/services/DocumentTemplateService";
import Multiselect from "@vueform/multiselect";
import IntermediaryService from '@/services/IntermediaryService';
import {useI18n} from "vue-i18n";
import StrategyService from "@/services/StrategyService";


const {t} = useI18n();
// Data z API
const investmentQuestionnaires = ref([])
const amlQuestionnaires = ref([])
const forms = ref([])
const meetingRecords = ref([])
const contractTypes = ref([]);
const strategies = ref([]);

// Kategorie
const categories = ref([])
const selectedCategoryId = ref('')

// Ovládání přidávání kategorie
const showAddCategory = ref(false)
const newCategoryName = ref('')

// Vybraná kategorie
const selectedCategory = computed(() =>
    categories.value?.find(c => c.id === selectedCategoryId.value)
)

const documentsTemplates = ref([]);
const intermediaries = ref([]); // options for intermediary multiselect

// Načtení existujících dat
onMounted(async () => {
  //const data = await getAssistedOnboardingSettings();

  const settings = await getSettingsValueByKey('assisted_onboarding_settings');
  categories.value = settings ? JSON.parse(settings) : [];
  // Ensure backward compatibility for newly added fields
  categories.value = categories.value.map(cat => ({
    ...cat,
    intermediary_ids: Array.isArray(cat?.intermediary_ids) ? cat.intermediary_ids : [],
    settings: {
      investment_questionnaires: [],
      aml_questionnaires: [],
      forms: [],
      meeting_records: [],
      identification: false,
      client_complex_form: false,
      address: false,
      bank_account: false,
      documents: false,
      calculation: false,
      strategies: false,
      contract_type: null,
      ...(cat?.settings || {})
    }
  }));
  //categories.value = data.value?.categories || []

  investmentQuestionnaires.value = await getFormsTemplatesByType('INVESTMENT_QUESTIONNAIRE');

  amlQuestionnaires.value = await getFormsTemplatesByType('AML_QUESTIONNAIRE');

  forms.value = await getFormsTemplatesByType('FORM');

  meetingRecords.value = await getFormsTemplatesByType('MEETING_RECORD');

  getContractTypes();
  await fetchStrategies();
  await getActiveDocumentsTemplates();
  await fetchIntermediaries();
})

function getSettingsValueByKey(key) {
  return SettingsService.getSettingsValueByKey(key)
      .then((response) => {
        return response[key];
      })
      .catch((error) => {
      });
}

// Potvrzení přidání nové kategorie
const confirmAddCategory = () => {
  const name = newCategoryName.value.trim()
  if (!name) return

  const newCategory = {
    id: Date.now(),
    name,
    intermediary_ids: [],
    settings: {
      investment_questionnaires: [],
      aml_questionnaires: [],
      forms: [],
      meeting_records: [],
      identification: false,
      client_complex_form: false,
      address: false,
      bank_account: false,
      documents: false,
      calculation: false,
      strategies: [],
      contract_type: null
    }
  }

  categories.value.push(newCategory)
  selectedCategoryId.value = newCategory.id
  newCategoryName.value = ''
  showAddCategory.value = false
}

// Zrušení přidávání
const cancelAddCategory = () => {
  newCategoryName.value = ''
  showAddCategory.value = false
}

const isSaving = ref(false);

// Uložení nastavení
function saveAssistedOnboardingSettings() {
  isSaving.value = true;
  const body = {
    'key': 'assisted_onboarding_settings',
    'value': JSON.stringify(categories.value)
  }
  SettingsService.saveOnboardingSettings(body)
      .then(() => {
        showSuccessMessage(t('settings.assisted_onboarding_settings_saved'));
        isSaving.value = false;
      })
      .catch((error) => {
        console.log(error);
      });
}

async function getFormsTemplatesByType(type) {
  const formTemplates = await FormTemplateService.getAllByType(type);
  return formTemplates;
}

function getContractTypes() {
  ContractTypeService.getAll().then((response) => {
    contractTypes.value = response;
  });
}

function handleCreateContractChoice() {
  if (!createContractChoice.value) {
    onboardingOptions.value.contract_type = null;
  }
}

async function getActiveDocumentsTemplates() {
  documentsTemplates.value = await DocumentTemplateService.getAllActive();
}

function deleteSelectedCategory() {
  if (!selectedCategory.value) return;
  const idToDelete = selectedCategory.value.id;

  const idx = categories.value.findIndex(c => c.id === idToDelete);
  if (idx !== -1) {
    categories.value.splice(idx, 1);
  }

  saveAssistedOnboardingSettings();
  // reset selection
  selectedCategoryId.value = '';
}

async function fetchIntermediaries() {
  // Load intermediaries options for selection
  try {
    const response = await IntermediaryService.getAll();
    intermediaries.value = response.map(intermediary => ({
      value: intermediary.id,
      label: intermediary.label
    }));
  } catch (e) {
    // silence
  }
}

async function fetchStrategies() {
  try {
    const response = await StrategyService.getAll('select');
    strategies.value = response.map(strategy => ({
      value: strategy.id,
      label: strategy.label
    }));
  } catch (e) {
    // silence
  }
}
</script>


<template>
  <div class="col-12">
    <div class="card mb-3">
      <div class="card-header">
        <div class="card-header__title">
          Nastavení asistovaného onboardingu
        </div>
      </div>

      <div class="card-body p-4">

        <!-- Výběr kategorie + přidání nové -->
        <div class="row mb-4 items-end">
          <div class="col-md-6 form-group">
            <label class="form-label">Kategorie</label>
            <div class="d-flex align-items-center gap-2">
              <select
                  class="form-select flex-grow-1"
                  v-model="selectedCategoryId"
              >
                <option value="">Vyber kategorii...</option>
                <option
                    v-for="category in categories"
                    :key="category.id"
                    :value="category.id"
                >
                  {{ category.name }}
                </option>
              </select>

              <button
                  type="button"
                  class="btn btn-outline-primary"
                  @click="showAddCategory = true"
              >
                <span class="m-0 p-0">+</span>
              </button>
              <button
                  v-if="selectedCategory"
                  type="button"
                  class="btn btn-outline-danger"
                  @click="deleteSelectedCategory"
              >
                <!--                <span class="m-0 p-0">🗑</span>-->
                <span class="mdi mdi-delete m-0 p-0"></span>
              </button>
            </div>

            <!-- Input pro novou kategorii -->
            <div v-if="showAddCategory" class="d-flex align-items-center gap-2 mt-2">
              <input
                  v-model="newCategoryName"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Název nové kategorie"
                  @keyup.enter="confirmAddCategory"
              />
              <button
                  class="btn btn-outline-primary"
                  type="button"
                  @click="confirmAddCategory"
              >
                Přidat
              </button>
              <button
                  class="btn btn-outline-danger"
                  type="button"
                  @click="cancelAddCategory"
              >
                Zrušit
              </button>
            </div>
          </div>
        </div>

        <!-- Formulář pro zvolenou kategorii -->
        <div v-if="selectedCategory">
          <!-- Edit category name -->
          <div class="row mb-3">
            <div class="col-md-6">
              <label class="form-label">Název kategorie</label>
              <input v-model="selectedCategory.name" type="text" class="form-control" placeholder="Zadejte název kategorie"/>
            </div>
          </div>

          <!-- Intermediaries selection for category -->
          <div class="row mb-4">
            <div class="col-md-6">
              <label class="form-label">Zprostředkovatelé</label>
              <Multiselect
                  v-model="selectedCategory.intermediary_ids"
                  :options="intermediaries"
                  :close-on-select="false"
                  :hide-selected="false"
                  placeholder="Vyberte zprostředkovatele"
                  mode="tags"
              />
            </div>
          </div>
          <div class="row">
            <div class="col-12 col-md-6">
              <OnboardingSettingsForm
                  v-model="selectedCategory.settings"
                  :investment-questionnaires="investmentQuestionnaires"
                  :aml-questionnaires="amlQuestionnaires"
                  :forms="forms"
                  :meeting-records="meetingRecords"
                  :contract-types="contractTypes"
                  :documents-templates="documentsTemplates"
                  :strategies="strategies"
              />
            </div>
          </div>

        </div>
      </div>

      <div class="card-footer">
        <div class="row">
          <div class="col-12 col-md-6 d-flex justify-content-end">
            <button class="btn btn-primary" type="button" @click="saveAssistedOnboardingSettings" :disabled="isSaving">
              {{ isSaving ? 'Ukládám' : 'Uložit' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>